import * as React from 'react';
import * as Yup from 'yup';
import * as acl from 'common/utils/acl';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Formik, FormikActions, FormikProps } from 'formik';
import { fromPhoneE164, toPhoneE164 } from 'common/utils/format';
import { mergeAddress, mergeFields, processApiError, addressValidationSchemaFactory } from 'common/utils/form';

import { AppState } from 'common/models/AppState';
import { FormAddressInput } from 'common/components/form/FormAddressInput';
import { FormChapterIdSelect } from 'common/components/form/FormChapterIdSelect';
import { FormChapterIdSelectAdmin } from 'common/components/form/FormChapterIdSelectAdmin';
import {
  ACCOUNT_MANAGER_NAMES,
  FUNCTIONAL_ROLE_OPTIONS,
  hasAccountManagementMemberType,
  ORGANIZATION_LEVEL_OPTIONS,
  SEGMENT_OPTIONS,
  SUBSEGMENT_OPTIONS,
} from 'common/utils/memberConstants';
import { FormHelp } from 'common/components/form/FormHelp';
import { OrgAccess } from 'common/api/models/OrgAccess';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { Member } from 'common/api/models/Member';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Analytics } from 'common/utils/Analytics';
import { FormInput } from 'common/components/form/FormInput';

let accessOptions: any = [];
const accesses = acl.getAdminAccesses();
for (let k in accesses) {
  if (accesses.hasOwnProperty(k)) {
    accessOptions.push({ label: accesses[k], value: k });
  }
}

interface UsersDialogProps {
  user?: Member;
  addOrgId?: string;
  segment?: string;
  subsegment?: string;
  appState: AppState;
  onSave: (id: string) => void;
  onCancel: () => void;
}

class UsersFormValues {
  firstName = '';
  lastName = '';
  email = '';
  secondaryEmail = undefined;
  homePhone = undefined;
  workPhone = undefined;
  mobilePhone = undefined;
  /* tslint:disable variable-name */
  home_street1 = '';
  home_street2 = '';
  home_city = '';
  home_state = '';
  home_postal = '';
  home_country = '';
  work_street1 = '';
  work_street2 = '';
  work_city = '';
  work_state = '';
  work_postal = '';
  work_country = '';
  billing_street1 = '';
  billing_street2 = '';
  billing_city = '';
  billing_state = '';
  billing_postal = '';
  billing_country = '';
  shipping_street1 = '';
  shipping_street2 = '';
  shipping_city = '';
  shipping_state = '';
  shipping_postal = '';
  shipping_country = '';
  status = '';
  adminAccess = 'NONE';
  chapterRole = 'MEMBER';
  chapterId = '';
  chapterAdmin = '';
  educationHours = '';
  certification = 'NONE';
  // certificationExpireDate = '';
  joinDate = '';
  kbis = '';
  gradDate = '';
  segment = '';
  subsegment = '';
  functionalRole = 'NONE';
  organizationLevel = 'NONE';
  social = {
    linkedin: '',
  };
  jobTitle = '';
  accountManager = '';
}

export class UsersDialog extends React.Component<UsersDialogProps> {
  validationSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string()
      .email()
      .lowercase()
      .required('Required'),
    secondaryEmail: Yup.string()
      .email()
      .lowercase(),
    homePhone: Yup.string().matches(/^[0-9 \-+\(\)\.]*$/, 'Invalid phone format'),
    workPhone: Yup.string().matches(/^[0-9 \-+\(\)\.]*$/, 'Invalid phone format'),
    mobilePhone: Yup.string().matches(/^[0-9 \-+\(\)\.]*$/, 'Invalid phone format'),
    ...addressValidationSchemaFactory('home'),
    ...addressValidationSchemaFactory('work'),
    ...addressValidationSchemaFactory('billing'),
    ...addressValidationSchemaFactory('shipping'),
    joinDate: Yup.date('Invalid date')
      .min('1963-01-01', 'Too far in the past')
      .max(new Date(), 'Must be in the past'),
    gradDate: Yup.date('Invalid date').min('1980-01-01', 'Too far in the past'),
    certification: Yup.string(),
    // certificationExpireDate: Yup.date().when('certification', {
    //   is: (certification: any) => certification !== 'NONE',
    //   then: Yup.date().required('Required'),
    // }),
  });

  initialValues() {
    return this.props.user
      ? mergeFields(new UsersFormValues(), {
          ...this.props.user,
          ...mergeAddress(this.props.user.homeAddress, 'home'),
          ...mergeAddress(this.props.user.workAddress, 'work'),
          ...mergeAddress(this.props.user.shippingAddress, 'shipping'),
          ...mergeAddress(this.props.user.billingAddress, 'billing'),
          ...this.props.user.vars,
          chapterRole:
            this.props.user.chapterPosition && this.props.user.chapterPosition.roleName === Member.ChapterRole.OFFICER
              ? this.props.user.chapterPosition!.positionName
              : undefined,
          homePhone: fromPhoneE164(this.props.user!.homePhone),
          workPhone: fromPhoneE164(this.props.user!.workPhone),
          mobilePhone: fromPhoneE164(this.props.user!.mobilePhone),
          segment: this.props.user.segment ? this.props.user.segment : this.props.segment ? this.props.segment : 'NONE',
          subsegment: this.props.user.subsegment
            ? this.props.user.subsegment
            : this.props.subsegment
              ? this.props.subsegment
              : 'NONE',
        })
      : new UsersFormValues();
  }

  handleSubmit = (values: UsersFormValues, actions: FormikActions<UsersFormValues>) => {
    const userData: NullableValues<Member> = {
      type: this.props.user && this.props.user.id ? undefined : Member.Type.USER, // only pass type for create
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      secondaryEmail: values.secondaryEmail,
      // for phones POST to invite seems to be treated differently than PATCH, so if value or this is create, go toPhoneE164 method (ie value || undefined)
      // but if no value and not a create send blank string, otherwise the API call doesn't include the field
      // create doesn't like empty string, but update treats it fine to erase the value there
      homePhone: values.homePhone || !(this.props.user && this.props.user.id) ? toPhoneE164(values.homePhone) : '',
      workPhone: values.workPhone || !(this.props.user && this.props.user.id) ? toPhoneE164(values.workPhone) : '',
      mobilePhone:
        values.mobilePhone || !(this.props.user && this.props.user.id) ? toPhoneE164(values.mobilePhone) : '',
      // homePhone: toPhoneE164(values.homePhone),
      // workPhone: toPhoneE164(values.workPhone),
      // mobilePhone: toPhoneE164(values.mobilePhone),
      homeAddress:
        values.home_street1 ||
        values.home_street2 ||
        values.home_city ||
        values.home_state ||
        values.home_country ||
        values.home_postal
          ? {
              street1: values.home_street1,
              street2: values.home_street2,
              city: values.home_city,
              state: values.home_state,
              country: values.home_country,
              postal: values.home_postal,
            }
          : null,
      workAddress:
        values.work_street1 ||
        values.work_street2 ||
        values.work_city ||
        values.work_state ||
        values.work_country ||
        values.work_postal
          ? {
              street1: values.work_street1,
              street2: values.work_street2,
              city: values.work_city,
              state: values.work_state,
              country: values.work_country,
              postal: values.work_postal,
            }
          : null,
      billingAddress:
        values.billing_street1 ||
        values.billing_street2 ||
        values.billing_city ||
        values.billing_state ||
        values.billing_country ||
        values.billing_postal
          ? {
              street1: values.billing_street1,
              street2: values.billing_street2,
              city: values.billing_city,
              state: values.billing_state,
              country: values.billing_country,
              postal: values.billing_postal,
            }
          : null,
      shippingAddress:
        values.shipping_street1 ||
        values.shipping_street2 ||
        values.shipping_city ||
        values.shipping_state ||
        values.shipping_country ||
        values.shipping_postal
          ? {
              street1: values.shipping_street1,
              street2: values.shipping_street2,
              city: values.shipping_city,
              state: values.shipping_state,
              country: values.shipping_country,
              postal: values.shipping_postal,
            }
          : null,
      orgs: this.props.addOrgId ? [{ memberId: this.props.addOrgId, access: OrgAccess.Access.NORMAL }] : [],
      status: Member.Status[values.status],
      chapterId: values.chapterId,
      chapterAdmin: values.chapterAdmin,
      vars: {
        kbis: values.kbis,
        gradDate: values.gradDate,
      },
      // certificationExpireDate:
      //   values.certificationExpireDate && values.certification === 'NONE' ? '' : values.certificationExpireDate,
      chapterPosition: {
        roleName: values.chapterRole === 'MEMBER' ? Member.ChapterRole.MEMBER : Member.ChapterRole.OFFICER,
        positionName: values.chapterRole !== 'MEMBER' ? values.chapterRole : '',
      },
      joinDate: values.joinDate ? values.joinDate : '',
      segment: values.segment ? values.segment : 'NONE',
      subsegment: values.subsegment ? values.subsegment : 'NONE',
      functionalRole: values.functionalRole,
      organizationLevel: values.organizationLevel,
      jobTitle: values.jobTitle,
      social: {
        linkedin: values.social.linkedin,
      },
    };
    if (hasAccountManagementMemberType(this.props.user && this.props.user.memberType)) {
      userData.accountManager = values.accountManager;
    }
    if (this.props.user && (!this.props.user.orgs || this.props.user.orgs.length === 0)) {
      userData.segment = values.segment;
      userData.subsegment = values.subsegment;
    }
    if (acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_USER_ACCESS)) {
      userData.adminAccess = Member.AdminAccess[values.adminAccess];
    }
    if (acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_CERT)) {
      userData.vars.certification = values.certification;
    }
    if (acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_EDU)) {
      userData.vars.educationHours = Number(values.educationHours);
    }
    const user = this.props.user!;
    let call;
    let analyticsEvent: () => void;
    if (this.props.addOrgId) {
      call = MemberApi.inviteUser(userData as Member);
      analyticsEvent = Analytics.member.invite;
    } else if (user.id) {
      call = MemberApi.updateMember(user.id, userData as Member);
      analyticsEvent = Analytics.member.update;
    } else {
      call = MemberApi.createMember(userData as Member);
      analyticsEvent = Analytics.member.create;
    }
    call.then(
      res => {
        this.props.onSave(user.id ? user.id : (res as any).id);
        analyticsEvent();
        actions.setSubmitting(false);
        actions.setStatus(undefined);
      },
      err => {
        processApiError(err, actions);
      }
    );
  };

  render() {
    const title = this.props.user && this.props.user.id ? 'Edit User' : 'Add User';

    return (
      <Modal size="lg" isOpen={!!this.props.user} toggle={this.props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues()}
          validationSchema={this.validationSchema}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<UsersFormValues>) => (
            <form onSubmit={form.handleSubmit} data-testid="usersDialog">
              <ModalHeader toggle={this.props.onCancel}>{title}</ModalHeader>
              {this.props.appState.user && this.props.appState.user.adminAccess !== Member.AdminAccess.CHAPTERS ? (
                <ModalBody>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        id="firstName"
                        data-testid="usersDialogInputFirstName"
                        label="First Name"
                        name="firstName"
                        autoComplete="off"
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        id="lastName"
                        data-testid="usersDialogInputLastName"
                        label="Last Name"
                        name="lastName"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        id="email"
                        data-testid="usersDialogInputEmail"
                        label="Email"
                        name="email"
                        autoComplete="off"
                      />
                    </div>
                    <div className="col">
                      <FormInput id="secondaryEmail" label="Secondary Email" name="secondaryEmail" autoComplete="off" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput id="homePhone" label="Home Phone" name="homePhone" autoComplete="off" />
                    </div>
                    <div className="col">
                      <FormInput id="workPhone" label="Work Phone" name="workPhone" autoComplete="off" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput id="mobilePhone" label="Mobile Phone" name="mobilePhone" autoComplete="off" />
                    </div>
                    <div className="col">
                      <FormInput
                        id="linkedin"
                        label="LinkedIn Profile"
                        name="social.linkedin"
                        placeholder="in/your-profile"
                        autoComplete="off"
                        data-testid="usersDialogInputLinkedin"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <FormAddressInput form={form} prefix="home" label="Home Address" />
                    </div>
                    <div className="col-12 col-md-6">
                      <FormAddressInput form={form} prefix="work" label="Work Address" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <FormAddressInput form={form} prefix="billing" label="Billing Address" />
                    </div>
                    <div className="col-12 col-md-6">
                      <FormAddressInput form={form} prefix="shipping" label="Mailing Address" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      <FormChapterIdSelect appState={this.props.appState} form={form} />
                    </div>
                    <div className="col col-6">
                      <FormInput id="chapterRole" label="Chapter Role" name="chapterRole" tag="select">
                        <option value="MEMBER">Member</option>
                        <option value="President">President</option>
                        <option value="Programs Chair">Programs Chair</option>
                        <option value="Membership Chair">Membership Chair</option>
                        <option value="Communications Chair">Communications Chair</option>
                        <option value="Secretary/Treasurer">Secretary/Treasurer</option>
                        {/* roles to be removed after 12/31/17 */}
                        <option value="VP Programs">VP Programs</option>
                        <option value="VP Communications">VP Communications</option>
                        <option value="VP Membership">VP Membership</option>
                        <option value="Secretary">Secretary</option>
                        <option value="Treasurer">Treasurer</option>
                        <option value="VP Academic Relations">VP Academic Relations</option>
                        <option value="VP Government Relations">VP Government Relations</option>
                        <option value="VP Professional Development">VP Professional Development</option>
                        <option value="Chapter Representative">Chapter Representative</option>
                      </FormInput>
                    </div>
                  </div>
                  {this.props.appState.user &&
                    (acl.isManager(this.props.appState.user) ||
                      this.props.appState.user.adminAccess === Member.AdminAccess.CHAPTERS) && (
                      <div className="row">
                        <div className="col">
                          <FormChapterIdSelectAdmin appState={this.props.appState} form={form} />
                        </div>
                      </div>
                    )}
                  <div className="row">
                    {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_MEMBER) && (
                      <div className="col col-3">
                        <FormInput
                          id="status"
                          label={
                            <React.Fragment>
                              Status
                              <FormHelp text="Active users are permitted to sign into the NKBA web app. Set to Inactive for users you want to prevent from logging in." />
                            </React.Fragment>
                          }
                          name="status"
                          tag="select"
                        >
                          <option value="ACTIVE">Active</option>
                          <option value="EXPIRED">Expired</option>
                          <option value="INACTIVE">Inactive</option>
                          <option value="REACTIVATING">Reactivating</option>
                        </FormInput>
                      </div>
                    )}
                    {acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_USER_ACCESS) && (
                      <div className="col col-3">
                        <FormInput id="adminAccess" label="AMS Admin Access" name="adminAccess" tag="select">
                          {accessOptions
                            .filter((v: any) => {
                              if (v.value === 'SUPERUSER') {
                                if (acl.hasPermission(this.props.appState, UserPermissions.Permissions.SUPERUSER)) {
                                  return v;
                                }
                              } else {
                                return v;
                              }
                            })
                            .map((a: any) => (
                              <option key={a.value} value={a.value}>
                                {a.label}
                              </option>
                            ))}
                        </FormInput>
                      </div>
                    )}
                    {acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_EDU) && (
                      <div className="col">
                        <FormInput id="educationHours" label="Educational Hours" name="educationHours" type="number" />
                      </div>
                    )}
                  </div>
                  <div className="row">
                    {acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_CERT) && (
                      <div className="col">
                        <FormInput id="certification" label="Certification" name="certification" tag="select">
                          <option value="NONE" />
                          <option>AKBD</option>
                          <option>CBD</option>
                          <option>CKD</option>
                          <option value="CKD-CBD">CKD + CBD</option>
                          <option>CKBD</option>
                          <option>CKBI</option>
                          <option>CMKBD</option>
                        </FormInput>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput id="kbis" label="KBIS" name="kbis" tag="select">
                        <option value="">None</option>
                        <option value="30U30">30U30</option>
                        <option value="BOD">BOD</option>
                        <option value="CO">CO</option>
                        <option value="DC">DC</option>
                        {/* <option value="EDUC">EDUC</option> */}
                        {/* <option value="INST">INST</option> */}
                        <option value="NL">NL</option>
                        <option value="PP">PP</option>
                        {/* <option value="SCO">SCO</option> */}
                        <option value="SDC">SDC</option>
                        {/* <option value="STU">STU</option> */}
                        <option value="VFTI">VFTI</option>
                        <option value="GC">GC</option>
                        <option value="GCMT">GCMT</option>
                        <option value="DCL">DCL</option>
                        <option value="GCNM">GCNM</option>
                        <option value="GCAdv">GCAdv</option>
                        <option value="GCAP">GCAP</option>
                      </FormInput>
                    </div>
                    <div className="col">
                      <FormInput id="joinDate" label="Join Date" name="joinDate" type="date" />
                    </div>
                    <div className="col">
                      <FormInput id="gradDate" label="Grad Date" name="gradDate" type="date" />
                    </div>
                  </div>
                  {form.status && <div className="alert alert-danger">{form.status}</div>}
                  <div className="row">
                    <div className="col col-6">
                      <FormInput
                        id="jobTitle"
                        data-testid="usersDialogInputJobTitle"
                        label="Job Title"
                        name="jobTitle"
                        autoComplete="off"
                      />
                    </div>
                    {hasAccountManagementMemberType(this.props.user && this.props.user.memberType) && (
                      <div className="col col-6">
                        <FormInput tag="select" name="accountManager" id="accountManager" label="Account Manager">
                          <option value="">None</option>
                          {ACCOUNT_MANAGER_NAMES.map(name => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </FormInput>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      <FormInput
                        tag="select"
                        name="segment"
                        id="segment"
                        label="Segment"
                        disabled={
                          this.props.addOrgId
                            ? true
                            : this.props.user && this.props.user.orgs && this.props.user.orgs.length > 0
                        }
                      >
                        {SEGMENT_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FormInput>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      <FormInput
                        tag="select"
                        name="subsegment"
                        id="subsegment"
                        label="Subsegment"
                        disabled={
                          this.props.addOrgId
                            ? true
                            : this.props.user && this.props.user.orgs && this.props.user.orgs.length > 0
                        }
                      >
                        <option value="NONE">No Subsegment Selected</option>
                        {SUBSEGMENT_OPTIONS[form.values.segment] &&
                          SUBSEGMENT_OPTIONS[form.values.segment].map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </FormInput>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      {form.values.segment && (
                        <FormInput tag="select" name="functionalRole" id="functionalRole" label="Functional Role">
                          {form.values.segment === 'IP' || form.values.segment === 'OTHER' ? (
                            <option value="NONE">None</option>
                          ) : (
                            <option value="NONE">No Functional Role Selected</option>
                          )}
                          {FUNCTIONAL_ROLE_OPTIONS[form.values.segment] &&
                            FUNCTIONAL_ROLE_OPTIONS[form.values.segment].map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </FormInput>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col col-6">
                      <FormInput
                        tag="select"
                        name="organizationLevel"
                        id="organizationLevel"
                        label="Organization Level"
                      >
                        {ORGANIZATION_LEVEL_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </FormInput>
                    </div>
                  </div>
                </ModalBody>
              ) : (
                <ModalBody>
                  <div className="row">
                    <div className="col">
                      <FormChapterIdSelectAdmin appState={this.props.appState} form={form} />
                    </div>
                  </div>
                </ModalBody>
              )}
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit" data-testid="usersDialogButtonPrimary">
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/UsersDialog.tsx